import { useSite } from "@/contexts/SiteContext";
import Hero from "@/components/sections/Hero";
import ServiceBar from "@/components/sections/ServiceBar";
import Benefits from "@/components/sections/Benefits";
import Problems from "@/components/sections/Problems";
import FitFilter from "@/components/sections/FitFilter";
import About from "@/components/sections/About";
import Method from "@/components/sections/Method";
import Techniques from "@/components/sections/Techniques";
import Services from "@/components/sections/Services";
import CoursesPreview from "@/components/sections/CoursesPreview";
import Reviews from "@/components/sections/Reviews";
import Diplomas from "@/components/sections/Diplomas";
import Downloads from "@/components/sections/Downloads";
import Community from "@/components/sections/Community";
import Faq from "@/components/sections/Faq";
import ContactForm from "@/components/sections/ContactForm";
import CtaBand from "@/components/sections/CtaBand";
import ForestBand from "@/components/sections/ForestBand";
import StatsBand from "@/components/sections/StatsBand";
import InvestBand from "@/components/sections/InvestBand";
import DynamicPagesPreview from "@/components/sections/DynamicPagesPreview";
import GenericSection from "@/components/sections/GenericSection";
import SectionStyles from "@/components/sections/SectionStyles";
import { HomeBarometer, HomeSession, HomePulseLive, HomeWhyForest, HomeStoriesBand, HomeCounty, HomeForDear, HomeMiniDemo, HomeNotUs, HomeGuarantee, HomePractice, HomeWelcomeBack, HomeEvents } from "@/components/home";

const REGISTRY = {
  hero: Hero,
  service_bar: ServiceBar,
  benefits: Benefits,
  problems: Problems,
  fit_filter: FitFilter,
  about: About,
  method: Method,
  techniques: Techniques,
  services: Services,
  courses_preview: CoursesPreview,
  reviews: Reviews,
  diplomas: Diplomas,
  downloads: Downloads,
  community: Community,
  faq: Faq,
  cta_band: CtaBand,
  forest_band: ForestBand,
  stats_band: StatsBand,
  invest_band: InvestBand,
  contact: ContactForm,
  // Secțiuni noi Dim 15 (implicit ascunse; Georgiana le pornește din live editor)
  home_barometer: HomeBarometer,
  home_session: HomeSession,
  home_pulse_live: HomePulseLive,
  home_why_forest: HomeWhyForest,
  home_stories: HomeStoriesBand,
  home_county: HomeCounty,
  home_for_dear: HomeForDear,
  home_mini_demo: HomeMiniDemo,
  home_not_us: HomeNotUs,
  home_guarantee: HomeGuarantee,
  home_practice: HomePractice,
  home_welcome_back: HomeWelcomeBack,
  home_events: HomeEvents,
};

// Secțiuni deținute de pagini/panouri dedicate (au randare proprie pe ruta lor).
// Ele sunt `visible: true` DOAR ca API-ul public să le întoarcă pentru editorul
// live + pagina proprie — dar NU trebuie să apară pe Home prin fallback-ul
// GenericSection. Fără această listă, /despre-mine (about_*), /cont (acc_*) și
// paginile convertite (/sedinte, /servicii) s-ar dubla vizual pe homepage.
const PAGE_OWNED = new Set([
  // header_cta nu e o secțiune de pagină — e doar configurarea celor 3 butoane
  // „Programează o sesiune" din Header.jsx (cta-spec.md secțiunea F.1); trebuie
  // vizibilă pentru sectionContent("header_cta"), dar NU randată ca bloc pe Home.
  "header_cta",
  "about_training", "about_cta",
  "sessions_hero", "sessions_why", "sessions_offers", "sessions_expect", "sessions_cta",
  "services_page", "forest_intro", "forest_cta", "breathe_page", "night_page",
  "acc_header", "acc_tabs", "acc_garden", "acc_dashboard",
  "acc_courses", "acc_sessions", "acc_live", "acc_profile",
  "techniques_page", "audio_page", "live_page", "library_page",
  "stories_page", "booking_page",
  "legal_termeni", "legal_politica-confidentialitate", "legal_politica-cookies",
  "legal_gdpr", "legal_disclaimer",
]);

export default function Home() {
  const { sections, loading } = useSite();
  if (loading) {
    return <div data-testid="home-loading" className="min-h-[60vh] flex items-center justify-center text-sm" style={{ color: "var(--ct-text-muted)" }}>Se încarcă...</div>;
  }
  const visible = sections.filter((s) => s.visible && !PAGE_OWNED.has(s.key)).sort((a, b) => a.order - b.order);
  // `sections` conține DOAR chei visible:true (filtrat server-side de /public/sections),
  // deci prezența cheii e suficientă — nu mai verificăm și .visible separat.
  // Implicit ASCUNS (vezi backend/deactivate_non_document_content.py) — documentele de
  // brand nu menționează pagini dinamice; Georgiana îl pornește din Admin → Secțiuni.
  const showDynamicPagesPreview = sections.some((s) => s.key === "dynamic_pages_preview");
  return (
    <div data-testid="home-page">
      {visible.map((s) => {
        const Comp = REGISTRY[s.key];
        // Known section → render as before. Unknown key (admin/wizard-created)
        // → fall back to GenericSection so it's visible on the public site.
        if (!Comp && !s.content) return null;
        return (
          <div key={s.key} data-section-key={s.key} data-section-title={s.title}>
            <SectionStyles scopeKey={s.key} content={s.content || {}} />
            {Comp ? <Comp /> : <GenericSection content={s.content} />}
          </div>
        );
      })}
      {showDynamicPagesPreview && (
        <div data-section-key="dynamic_pages_preview" data-section-title="Pagini dinamice (preview pe Home)">
          <DynamicPagesPreview />
        </div>
      )}
    </div>
  );
}
